import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  SecurityContext,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { Widget } from '../../../models/form-widget.model';
import { WidgetBase } from '../../../models/widget-base';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { RxRicheditComponent } from '../../../../../../rx-ui/src/lib/rx-richedit/rx-richedit.component';

@Component({
  selector: 'app-fb-richedit',
  templateUrl: './fb-richedit.component.html',
  styleUrls: ['./fb-richedit.component.scss'],
  imports: [CommonModule, FormsModule, TokenToDivPipe, RxRicheditComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbRicheditComponent extends WidgetBase {
  private readonly alignedTextTagPattern =
    /<(figure|p|li|span|h[1-6]|td|th|tr|div|ul|ol)\b([^>]*)>/gi;
  private readonly styleAttributePattern = /\sstyle="([^"]*)"/i;
  private readonly allowedTextAlignValues = ['left', 'right', 'center', 'justify'];
  private readonly allowedTextDecorationValues = [
    'underline',
    'line-through',
    'overline',
    'none',
  ];

  public readonly = true;
  private _payloadOriginal;
  public html = new BehaviorSubject<SafeHtml>(<any>null);
  @ViewChild('propertyDynamic2') private richeditRef?: RxRicheditComponent;
  // @ts-nocheck
  @Input() public override set payload(value) {
    this._payload = value;
    this.html.next(this.getHtml(this.payload.properties.value.value));
    this.ready.next(true);
  }
  public override get payload() {
    return this._payload;
  }

  public override set selected(value: boolean) {
    if (this._selected && !value) {
      this.richeditRef?.suppressPendingChanges();
    }
    this._selected = value;
    this.html.next(this.getHtml(this.payload.properties.value.value));
    this._cd.detectChanges();
  }
  public override get selected() {
    return this._selected;
  }

  constructor(
    public sanitizer: DomSanitizer,
    private _cd: ChangeDetectorRef,
    private injector: Injector,
  ) {
    super(injector);
  }

  public getHtml(template) {
    const decodedTemplate = template.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    const templateWithTextAlignStyles = this.keepOnlyTextAlignStyles(decodedTemplate);
    const textStyles = this.collectTextStyles(templateWithTextAlignStyles);

    const sanitizedTemplate =
      this.sanitizer.sanitize(SecurityContext.HTML, templateWithTextAlignStyles) || '';
    return this.sanitizer.bypassSecurityTrustHtml(
      this.restoreTextStyles(sanitizedTemplate, textStyles),
    );
  }

  private keepOnlyTextAlignStyles(template: string): string {
    return template.replace(
      /<([a-z][a-z0-9]*)([^>]*)\sstyle="([^"]*)"([^>]*)>/gi,
      (match, tagName, attributesBeforeStyle, styleValue, attributesAfterStyle) => {
        const textAlign = this.getAllowedTextAlign(styleValue);
        const textDecoration = this.getTextDecoration(styleValue);

        const styles = [];
        if (this.supportsTextAlign(tagName) && textAlign) {
          styles.push(`text-align: ${textAlign}`);
        }
        if (textDecoration) {
          styles.push(`text-decoration: ${textDecoration}`);
        }

        if (styles.length > 0) {
          return `<${tagName}${attributesBeforeStyle} style="${styles.join('; ')};"${attributesAfterStyle}>`;
        }
        return `<${tagName}${attributesBeforeStyle}${attributesAfterStyle}>`;
      },
    );
  }

  private collectTextStyles(
    template: string,
  ): Array<{ align: string | null; decoration: string | null }> {
    const textStyles: Array<{ align: string | null; decoration: string | null }> = [];

    template.replace(this.alignedTextTagPattern, (match) => {
      const styleMatch = match.match(this.styleAttributePattern);
      if (styleMatch) {
        textStyles.push({
          align: this.getAllowedTextAlign(styleMatch[1]),
          decoration: this.getTextDecoration(styleMatch[1]),
        });
      } else {
        textStyles.push({ align: null, decoration: null });
      }
      return match;
    });

    return textStyles;
  }

  private restoreTextStyles(
    template: string,
    textStyles: Array<{ align: string | null; decoration: string | null }>,
  ): string {
    let styleIndex = 0;

    return template.replace(this.alignedTextTagPattern, (match, tagName, attributes) => {
      const styles = textStyles[styleIndex++];
      if (!styles || (!styles.align && !styles.decoration)) {
        return match;
      }

      const attributesWithoutStyle = attributes.replace(this.styleAttributePattern, '');
      const styleList = [];
      if (styles.align) styleList.push(`text-align: ${styles.align}`);
      if (styles.decoration) styleList.push(`text-decoration: ${styles.decoration}`);

      return `<${tagName}${attributesWithoutStyle} style="${styleList.join('; ')};">`;
    });
  }

  private supportsTextAlign(tagName: string): boolean {
    return /^(figure|p|li|span|h[1-6]|td|th|tr|div|ul|ol)$/i.test(tagName);
  }

  private getAllowedTextAlign(styleValue: string): string | null {
    const textAlignMatch = styleValue.match(/(?:^|;)\s*text-align\s*:\s*([^;"]+)/i);
    const textAlign = textAlignMatch?.[1]?.trim().toLowerCase();

    return textAlign && this.allowedTextAlignValues.includes(textAlign) ? textAlign : null;
  }

  private getTextDecoration(styleValue: string): string | null {
    const decorationMatch = styleValue.match(/(?:^|;)\s*text-decoration\s*:\s*([^;"]+)/i);
    const decoration = decorationMatch?.[1]?.trim().toLowerCase();

    if (!decoration) {
      return null;
    }

    const tokens = decoration.split(/\s+/);
    const isAllowed = tokens.every((token) => this.allowedTextDecorationValues.includes(token));

    return isAllowed ? decoration : null;
  }
}
